import { ResizeNotifier } from '@theme/utilities';
import { DeclarativeShadowElement } from '@theme/component';

/**
 * Event class for overflow minimum items updates
 * @extends {Event}
 */
export class OverflowMinimumEvent extends Event {
  /**
   * Creates a new OverflowMinimumEvent
   * @param {boolean} minimumReached - Whether the minimum number of visible items has been reached
   */
  constructor(minimumReached) {
    super('overflowMinimum', { bubbles: true });
    this.detail = {
      minimumReached,
    };
  }
}

/**
 * A custom element that wraps a list of items and moves them to an overflow slot when they don't fit.
 * This component is used in the header section and other areas.
 * @attr {string | null} minimum-items When set, the element enters a 'minimum-reached' state when visible items are at or below this number.
 * @attr {string | null} continue-after-logo When set on a header menu, items continue in the free space after a centered logo before overflowing.
 * @example
 * <overflow-list minimum-items="2">
 *   <!-- list items -->
 * </overflow-list>
 */
export class OverflowList extends DeclarativeShadowElement {
  static get observedAttributes() {
    return ['continue-after-logo', 'disabled', 'minimum-items'];
  }

  /**
   * @param {string} name
   * @param {string} oldValue
   * @param {string} newValue
   */
  attributeChangedCallback(name, oldValue, newValue) {
    if (name === 'disabled') {
      if (newValue === 'true') {
        this.#reset();
      } else {
        this.#reflowItems();
      }
    } else if (name === 'continue-after-logo' && this.#refs) {
      this.#handleChange();
    }
  }

  async connectedCallback() {
    super.connectedCallback();

    // Styles for dynamically injected <overflow-list> elements are async.
    // We need to wait for them to be loaded before initializing the element to properly calculate the overflow.
    await this.#waitForStyles();

    this.#initialize();
  }

  #waitForStyles() {
    /** @type {HTMLLinkElement | null | undefined} */
    const styles = this.shadowRoot?.querySelector('link[rel="stylesheet"]');

    // No styles or styles are already loaded.
    if (!styles || styles.sheet) {
      return Promise.resolve();
    }

    return new Promise((resolve) => {
      styles.addEventListener('load', resolve);
    });
  }

  /**
   * Initialize the element
   */
  #initialize() {
    const { shadowRoot } = this;

    if (!shadowRoot) throw new Error('Missing shadow root');

    const defaultSlot = shadowRoot.querySelector('slot:not([name])');
    const overflowSlot = shadowRoot.querySelector('slot[name="overflow"]');
    const moreSlot = shadowRoot.querySelector('slot[name="more"]');
    const overflow = shadowRoot.querySelector('[part="overflow"]');
    const list = shadowRoot.querySelector('[part="list"]');
    const placeholder = shadowRoot.querySelector('[part="placeholder"]');
    const row = this.closest('.header__row');
    const continuationPortal = row?.querySelector('[data-menu-continuation-portal]');
    const continuationList = continuationPortal?.querySelector('[data-menu-continuation-list]');
    const externalOverflowPortal = continuationPortal?.querySelector('[data-menu-overflow-portal]');
    const externalOverflowList = externalOverflowPortal?.querySelector('[data-menu-overflow-list]');
    const moreElement = this.querySelector(':scope > [data-overflow-list-more], :scope > [slot="more"]');

    if (
      !(defaultSlot instanceof HTMLSlotElement) ||
      !(overflowSlot instanceof HTMLSlotElement) ||
      !(moreSlot instanceof HTMLSlotElement) ||
      !(overflow instanceof HTMLElement) ||
      !(list instanceof HTMLUListElement) ||
      !(placeholder instanceof HTMLLIElement)
    ) {
      throw new Error('Invalid element types in <OverflowList />');
    }

    this.#refs = {
      defaultSlot,
      overflowSlot,
      moreSlot,
      overflow,
      list,
      continuationPortal: continuationPortal instanceof HTMLElement ? continuationPortal : null,
      continuationList: continuationList instanceof HTMLUListElement ? continuationList : null,
      externalOverflowPortal: externalOverflowPortal instanceof HTMLElement ? externalOverflowPortal : null,
      externalOverflowList: externalOverflowList instanceof HTMLUListElement ? externalOverflowList : null,
      moreElement: moreElement instanceof HTMLElement ? moreElement : null,
      placeholder,
    };

    // Add event listener for reflow requests
    this.addEventListener(
      'reflow',
      /** @param {CustomEvent<{lastVisibleElement?: HTMLElement}>} event */ (event) => {
        this.#reflowItems(0, event.detail.lastVisibleElement);
      }
    );

    // When <overflow-list> is dynamically injected, the browser doesn't remove its <template> automatically.
    // In theory, we could get rid of it now, or in DeclarativeShadowElement, but that would invalidate the layout.
    // Instead, we ignore it for now and remove it later on the first reflow.
    const elements = defaultSlot.assignedElements().filter((element) => !(element instanceof HTMLTemplateElement));
    this.#menuItems = elements;
    const firstElement = elements[0];
    const lastElement = elements[elements.length - 1];

    // Observe the first and last elements to trigger a reflow when they are visible.
    // That way we can get their height from the IntersectionObserver for free (without reflows).
    if (firstElement) {
      this.#intersectionObserver.observe(firstElement);
    }
    if (lastElement && lastElement !== firstElement) {
      this.#intersectionObserver.observe(lastElement);
    }

    document.fonts?.ready.then(this.#handleChange);

    // The original one-rail More menu can wait for IntersectionObserver because
    // its initial overflow is clipped in place. Split navigation reparents links
    // across the logo, so calculate it immediately before revealing the rails.
    if (this.continuesAfterLogo) {
      this.#reflowItems();
    }
  }

  disconnectedCallback() {
    if (this.#refs) this.#reset();
    this.#resizeObserver.disconnect();
    this.#mutationObserver.disconnect();
    this.#intersectionObserver.disconnect();
  }

  /**
   * Restore portalled nodes before the theme morphs fresh section HTML into this component.
   * The morph integration calls this synchronously before it compares child nodes.
   */
  prepareForMorph() {
    if (!this.#refs) return;

    this.closest('header-menu[data-continue-after-logo]')?.removeAttribute('data-overflow-ready');
    this.#reset();
    queueMicrotask(this.#reflowItems);
  }

  get schedule() {
    return typeof Theme?.utilities?.scheduler?.schedule === 'function'
      ? Theme.utilities.scheduler.schedule
      : /** @param {FrameRequestCallback} callback */ (callback) =>
          requestAnimationFrame(() => setTimeout(callback, 0));
  }

  #scheduled = false;

  /**
   * Get the minimum number of items before changing the minimum-reached state
   * @returns {number | null}
   */
  get minimumItems() {
    const value = this.getAttribute('minimum-items');
    return value ? parseInt(value, 10) : null;
  }

  get overflowSlot() {
    const { overflowSlot } = this.#refs;
    return overflowSlot;
  }

  get defaultSlot() {
    const { defaultSlot } = this.#refs;
    return defaultSlot;
  }

  /**
   * Whether header items should continue on the opposite side of the centered logo.
   * @returns {boolean}
   */
  get continuesAfterLogo() {
    return (
      this.hasAttribute('continue-after-logo') ||
      this.closest('header-menu')?.hasAttribute('data-continue-after-logo')
    );
  }

  /**
   * @param {IntersectionObserverEntry[]} entries
   */
  #handleIntersection = (entries) => {
    const entry = entries[0];
    if (entry?.isIntersecting) {
      this.#intersectionObserver.disconnect();
      setTimeout(() => {
        // Remove the leftover <template> for dynamically injected <overflow-list> elements.
        this.querySelector(':scope > template[shadowrootmode="open"]')?.remove();
        this.#reflowItems(entry.boundingClientRect.height);
      }, 0);
    }
  };

  /**
   * @type {ResizeObserverCallback & MutationCallback}
   */
  #handleChange = () => {
    if (this.#scheduled || !this.isConnected) return;

    this.#scheduled = true;

    requestAnimationFrame(() =>
      setTimeout(() => {
        this.#reflowItems();
        this.#scheduled = false;
      }, 0)
    );
  };

  /**
   * Move all items to the default slot.
   */
  #moveItemsToDefaultSlot() {
    const { defaultSlot, continuationList } = this.#refs;
    const currentMoreElement =
      this.querySelector(':scope > [data-overflow-list-more]') ??
      continuationList?.querySelector(':scope > [data-overflow-list-more]') ??
      this.querySelector(':scope > [slot="more"]') ??
      continuationList?.querySelector(':scope > [slot="more"]');
    const moreElement = currentMoreElement instanceof HTMLElement ? currentMoreElement : this.#refs.moreElement;
    this.#refs.moreElement = moreElement;
    if (moreElement) moreElement.slot = this.#refs.moreSlot.name;

    const directItems = [...this.children].filter(
      (element) => element !== moreElement && !(element instanceof HTMLTemplateElement)
    );
    const connectedItems = this.#menuItems.filter((element) => element.isConnected && element !== moreElement);
    const hasExternalItems = connectedItems.some((element) => element.parentElement !== this);

    if (!hasExternalItems) {
      // Preserve the generic component's support for children added after initialization.
      this.#menuItems = directItems;
    } else {
      this.#menuItems = connectedItems;
      for (const element of directItems) {
        if (!this.#menuItems.includes(element)) this.#menuItems.push(element);
      }
    }

    // Continuation items live after the logo in the light DOM. Restore the original
    // item order before each measurement so changing modes and section morphs are safe.
    if (moreElement && moreElement.parentElement !== this) {
      this.append(moreElement);
    }

    for (const element of this.#menuItems) {
      if (moreElement) {
        this.insertBefore(element, moreElement);
      } else {
        this.append(element);
      }

      if (element.slot !== defaultSlot.name) {
        element.slot = defaultSlot.name;
      }
    }
  }

  /**
   * Put the shared More slot and placeholder back in the primary list and hide the continuation portal.
   */
  #restorePrimaryList() {
    const {
      list,
      continuationPortal,
      continuationList,
      externalOverflowPortal,
      externalOverflowList,
      moreElement,
      moreSlot,
      placeholder,
    } = this.#refs;

    list.append(moreSlot, placeholder);
    moreSlot.hidden = true;
    placeholder.hidden = true;

    if (moreElement) {
      moreElement.hidden = false;
      moreElement.style.removeProperty('order');
    }

    if (continuationPortal) continuationPortal.hidden = true;
    continuationList?.removeAttribute('style');
    if (externalOverflowPortal) externalOverflowPortal.hidden = true;
    externalOverflowList?.removeAttribute('style');
  }

  /**
   * Reset the list to its initial state and disconnect the observers.
   */
  #reset() {
    const { list } = this.#refs;

    this.#unobserveChanges();
    this.#moveItemsToDefaultSlot();
    this.#restorePrimaryList();

    list.style.removeProperty('height');
    list.style.removeProperty('flex-wrap');
    list.style.removeProperty('overflow');
    list.style.removeProperty('counter-reset');
    this.style.setProperty('--overflow-count', '0');
    this.closest('header-menu')?.style.setProperty('--overflow-count', '0');
  }

  /**
   * Sets the minimum-reached attribute and dispatches a custom event based on visible elements count
   * @param {Element[]} visibleElements - The currently visible elements
   */
  #updateMinimumReached(visibleElements) {
    if (this.minimumItems !== null) {
      const minimumReached = visibleElements.length < this.minimumItems;

      if (minimumReached) {
        this.setAttribute('minimum-reached', '');
      } else {
        this.removeAttribute('minimum-reached');
      }

      this.dispatchEvent(new OverflowMinimumEvent(minimumReached));
    }
  }

  /**
   * Show all items in the list.
   */
  showAll() {
    const { placeholder } = this.#refs;

    placeholder.style.setProperty('width', '0');
    placeholder.style.setProperty('display', 'none');
    this.setAttribute('disabled', 'true');
  }

  /**
   * Reflow items based on available space within the list.
   * @param {number} [listHeight] Initial height of the list
   * @param {HTMLElement | null} [lastVisibleElement] Optional element to place in last visible position
   */
  #reflowItems = (listHeight = 0, lastVisibleElement = null) => {
    this.#unobserveChanges();

    // Reset all elements to the default slot so we can check which ones overflow.
    this.#moveItemsToDefaultSlot();
    this.#restorePrimaryList();

    const elements = this.#menuItems.filter((element) => element.isConnected);

    if (!elements.length) {
      this.#observeChanges();
      this.#markSplitMenuReady();
      return;
    }

    const continuedAfterLogo = this.continuesAfterLogo && this.#reflowItemsAcrossLogo(elements, listHeight);

    if (!continuedAfterLogo) {
      this.#moveItemsToDefaultSlot();
      this.#restorePrimaryList();
      this.#reflowItemsInPrimaryList(elements, listHeight, lastVisibleElement);
    }

    this.#observeChanges();
    this.#markSplitMenuReady();
  };

  /**
   * Reveal split navigation after its two rails reach a stable state.
   * The original one-rail overflow mode remains unchanged.
   */
  #markSplitMenuReady() {
    this.closest('header-menu[data-continue-after-logo]')?.setAttribute('data-overflow-ready', '');
  }

  /**
   * Use the original one-rail overflow behavior.
   * @param {Element[]} elements
   * @param {number} listHeight
   * @param {HTMLElement | null} lastVisibleElement
   */
  #reflowItemsInPrimaryList(elements, listHeight, lastVisibleElement) {
    const { defaultSlot, overflowSlot, moreSlot, list, placeholder } = this.#refs;

    this.#setMoreControls(this);

    /** @type {Element[]} */
    let visibleElements = [];
    /** @type {Element[]} */
    let overflowingElements = [];
    let placeholderWidth = 0;
    let hasOverflow = false;

    if (listHeight > 0) {
      list.style.setProperty('height', `${listHeight}px`);
    }

    // Enable flex-wrap so overflowing items break to the next line. This makes calculations easier.
    list.style.setProperty('flex-wrap', 'wrap');
    placeholder.hidden = true;

    // Putting the "More" item (and lastVisibleElement, if provided) at the start of the list lets us see which items will fit on the same row.
    moreSlot.style.setProperty('order', '-1');
    moreSlot.hidden = false;

    lastVisibleElement?.style.setProperty('order', '-1');

    const moreSlotRect = moreSlot.getBoundingClientRect();

    elements.forEach((element) => {
      const elementRect = element.getBoundingClientRect();

      if (elementRect.top > moreSlotRect.top) {
        if (!overflowingElements.length) {
          placeholderWidth = elementRect.width;
        }

        hasOverflow = true;
        overflowingElements.push(element);
      } else {
        visibleElements.push(element);
      }
    });

    if (hasOverflow) {
      moreSlot.style.removeProperty('order');
    }
    lastVisibleElement?.style.removeProperty('order');

    // Move the elements to the correct slot.
    for (const element of elements) {
      const targetSlot = overflowingElements.includes(element) ? overflowSlot.name : defaultSlot.name;
      if (element.slot !== targetSlot) {
        element.slot = targetSlot;
      }
    }

    list.style.setProperty('counter-reset', `overflow-count ${overflowingElements.length}`);
    this.style.setProperty('--overflow-count', `${overflowingElements.length}`);

    // Adjust the "More" button visibility.
    moreSlot.hidden = !hasOverflow;

    if (hasOverflow) {
      // Set the width and height of the placeholder so the list can grow if there is space.
      placeholder.style.width = `${placeholderWidth}px`;
      placeholder.hidden = false;
    }

    // Reset the overflow property since children elements may need to display outside the list (e.g. dropdowns, popovers).
    list.style.setProperty('overflow', 'unset');

    hasOverflow && this.#updateMinimumReached(visibleElements);
  }

  /**
   * Continue a contiguous suffix of header links in the free space after the centered logo.
   * Any links that do not fit there still move to the existing More menu.
   * @param {Element[]} elements
   * @param {number} listHeight
   * @returns {boolean} Whether the split layout was successfully applied.
   */
  #reflowItemsAcrossLogo(elements, listHeight) {
    const {
      overflowSlot,
      moreSlot,
      list,
      continuationPortal,
      continuationList,
      externalOverflowPortal,
      externalOverflowList,
      moreElement,
      placeholder,
    } = this.#refs;

    if (
      !continuationPortal ||
      !continuationList ||
      !externalOverflowPortal ||
      !externalOverflowList ||
      !moreElement
    ) {
      return false;
    }

    moreSlot.hidden = true;
    placeholder.hidden = true;

    // Measure the primary rail without reserving space for More: overflow continues after the logo instead.
    list.style.setProperty('flex-wrap', 'nowrap');
    const rowHeight = listHeight || list.getBoundingClientRect().height;
    if (rowHeight <= 0) return false;

    list.style.setProperty('height', `${rowHeight}px`);
    list.style.setProperty('flex-wrap', 'wrap');
    list.style.setProperty('overflow', 'hidden');

    // Horizontal bounds catch a single, exceptionally wide first label that flex-wrap
    // alone cannot move to a second line. Once one item does not fit, keep the suffix together.
    const continuationElements = this.#findOverflowingElements(elements, list);
    const continuationSet = new Set(continuationElements);
    const primaryElements = elements.filter((element) => !continuationSet.has(element));

    if (!continuationElements.length) {
      this.#setMoreControls(externalOverflowPortal);
      list.style.setProperty('counter-reset', 'overflow-count 0');
      this.style.setProperty('--overflow-count', '0');
      this.closest('header-menu')?.style.setProperty('--overflow-count', '0');
      list.style.setProperty('overflow', 'unset');
      return true;
    }

    const sourceMenu = this.closest('.menu-list');
    if (sourceMenu instanceof HTMLElement) {
      continuationPortal.style.cssText = sourceMenu.style.cssText;
    }

    externalOverflowPortal.className = 'header-menu__overflow-portal';
    for (const className of this.classList) {
      if (className.startsWith('color-')) externalOverflowPortal.classList.add(className);
    }

    continuationPortal.hidden = false;
    continuationList.style.setProperty('height', `${rowHeight}px`);
    continuationList.style.setProperty('direction', getComputedStyle(this).direction);
    continuationList.style.setProperty('flex-wrap', 'wrap');
    continuationList.style.setProperty('overflow', 'hidden');

    for (const element of continuationElements) {
      element.slot = 'continuation';
      continuationList.append(element);
    }

    continuationList.append(moreElement);
    moreElement.hidden = true;

    // First measure links alone. Reserve More only when some links genuinely do not fit.
    let overflowingElements = this.#findOverflowingElements(continuationElements, continuationList);

    if (overflowingElements.length) {
      moreElement.hidden = false;
      moreElement.style.setProperty('order', '-1');

      const moreRect = moreElement.getBoundingClientRect();
      const continuationRect = continuationList.getBoundingClientRect();
      if (
        moreRect.width <= 0 ||
        moreRect.left < continuationRect.left - 1 ||
        moreRect.right > continuationRect.right + 1
      ) {
        moreElement.style.removeProperty('order');
        return false;
      }

      overflowingElements = this.#findOverflowingElements(continuationElements, continuationList, moreRect.top);
      moreElement.style.removeProperty('order');
    }

    for (const element of overflowingElements) {
      element.slot = overflowSlot.name;
      externalOverflowList.append(element);
    }

    const overflowingSet = new Set(overflowingElements);
    const visibleContinuationElements = continuationElements.filter((element) => !overflowingSet.has(element));
    const hasOverflow = overflowingElements.length > 0;

    list.style.setProperty('counter-reset', `overflow-count ${overflowingElements.length}`);
    this.style.setProperty('--overflow-count', `${overflowingElements.length}`);
    this.closest('header-menu')?.style.setProperty('--overflow-count', `${overflowingElements.length}`);
    moreElement.hidden = !hasOverflow;
    externalOverflowPortal.hidden = !hasOverflow;
    const overflowExpanded = this.closest('header-menu')?.dataset.overflowExpanded === 'true';
    moreElement.querySelector('button')?.setAttribute('aria-expanded', String(hasOverflow && overflowExpanded));
    this.#setMoreControls(externalOverflowPortal);
    placeholder.hidden = true;

    // Allow mega menus and focus outlines to paint outside both one-line rails after measuring.
    list.style.setProperty('overflow', 'unset');
    continuationList.style.setProperty('overflow', 'unset');

    if (hasOverflow) {
      this.#updateMinimumReached([...primaryElements, ...visibleContinuationElements]);
    }

    return true;
  }

  /**
   * Keep the disclosure target aligned with whichever overflow panel is active.
   * @param {HTMLElement} target
   */
  #setMoreControls(target) {
    if (!target.id) return;

    this.#refs.moreElement?.querySelector('button')?.setAttribute('aria-controls', target.id);
  }

  /**
   * Return the contiguous suffix that wrapped or exceeded a rail's horizontal bounds.
   * @param {Element[]} elements
   * @param {HTMLElement} rail
   * @param {number | null} [referenceTop]
   * @returns {Element[]}
   */
  #findOverflowingElements(elements, rail, referenceTop = null) {
    const railRect = rail.getBoundingClientRect();
    const firstRect = elements[0]?.getBoundingClientRect();
    const rowTop = referenceTop ?? firstRect?.top;
    if (rowTop === undefined || railRect.width <= 0) return [...elements];

    /** @type {Element[]} */
    const overflowingElements = [];
    let suffixStarted = false;

    for (const element of elements) {
      const rect = element.getBoundingClientRect();
      const wrapped = rect.top > rowTop + 1;
      const outsideBounds = rect.left < railRect.left - 1 || rect.right > railRect.right + 1;

      if (suffixStarted || wrapped || outsideBounds) {
        suffixStarted = true;
        overflowingElements.push(element);
      }
    }

    return overflowingElements;
  }

  #observeChanges() {
    this.#resizeObserver.observe(this);
    this.#mutationObserver.observe(this, { childList: true });

    const headerMenu = this.closest('header-menu');
    if (headerMenu instanceof HTMLElement) {
      this.#mutationObserver.observe(headerMenu, {
        attributes: true,
        attributeFilter: ['data-continue-after-logo'],
      });
    }

    if (!this.continuesAfterLogo) return;

    const row = this.closest('.header__row');
    const logo = row?.querySelector('.header-logo');
    const rightColumn = row?.querySelector('.header__column--right');

    if (logo instanceof HTMLElement) {
      this.#resizeObserver.observe(logo);
    }

    if (rightColumn instanceof HTMLElement) {
      this.#resizeObserver.observe(rightColumn);
      for (const child of rightColumn.children) {
        if (child instanceof HTMLElement && !child.matches('[data-menu-continuation-portal]')) {
          this.#resizeObserver.observe(child);
        }
      }

      this.#mutationObserver.observe(rightColumn, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['class', 'hidden', 'style'],
      });
    }
  }

  #unobserveChanges() {
    this.#resizeObserver.disconnect();
    this.#mutationObserver.disconnect();
  }

  /**
   * @type {{
   *   defaultSlot: HTMLSlotElement;
   *   overflowSlot: HTMLSlotElement;
   *   moreSlot: HTMLSlotElement;
   *   overflow: HTMLElement;
   *   list: HTMLUListElement;
   *   continuationPortal: HTMLElement | null;
   *   continuationList: HTMLUListElement | null;
   *   externalOverflowPortal: HTMLElement | null;
   *   externalOverflowList: HTMLUListElement | null;
   *   moreElement: HTMLElement | null;
   *   placeholder: HTMLLIElement;
   * }}
   */
  #refs;

  /** @type {Element[]} */
  #menuItems = [];

  /**
   * @type {ResizeObserver}
   */
  #resizeObserver = new ResizeNotifier(this.#handleChange);

  /**
   * @type {MutationObserver}
   */
  #mutationObserver = new MutationObserver(this.#handleChange);

  #intersectionObserver = new IntersectionObserver(this.#handleIntersection, {
    // Extend the root margin to around one more viewport of a typical mobile screen.
    rootMargin: '640px 360px 640px 360px',
  });
}

if (!customElements.get('overflow-list')) {
  customElements.define('overflow-list', OverflowList);
}
